import React, { createContext, useContext, useRef, useEffect } from "react";
import { HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import api from "../API/axios";

const SignalRContext = createContext(null);

export const SignalRProvider = ({ accessToken, children }) => {

  const apiUrl = process.env.REACT_APP_URL;

  // Store operator info and assigned client/group IDs
  const [operatorInfo, setOperatorInfo] = React.useState(null);
  const joinedClientIdsRef = useRef(new Set());
  
  // Notification deduplication
  const lastNotificationRef = useRef(null);
  const notificationCooldownRef = useRef(null);
  const NOTIFICATION_COOLDOWN = 2000; // 2 seconds between similar notifications

  // Helper function to show notifications with deduplication
  const showNotification = (title, body) => {
    const notificationKey = `${title}:${body}`;
    const now = Date.now();
    
    // Check if this is a duplicate notification within cooldown period
    if (lastNotificationRef.current === notificationKey && 
        notificationCooldownRef.current && 
        (now - notificationCooldownRef.current) < NOTIFICATION_COOLDOWN) {
      return;
    }
    
    // Update deduplication tracking
    lastNotificationRef.current = notificationKey;
    notificationCooldownRef.current = now;
    
    if (!window.Notification) {
      return;
    }

    if (Notification.permission === 'granted') {
      try {
        const notification = new Notification(title, {
          body: body,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: 'webchat-message', // Prevents notification spam
          requireInteraction: false, // Auto-dismiss after default time
          silent: false, // Allow notification sound (in addition to our custom sound)
          timestamp: Date.now()
        });
        
        notification.onclick = () => {
          window.focus();
          notification.close();
        };

        notification.onerror = (error) => {
          console.error('[SignalRProvider] Notification error:', error);
        };
        
        // Auto-close after 8 seconds
        setTimeout(() => {
          notification.close();
        }, 8000);
      } catch (error) {
        console.error('[SignalRProvider] Error creating notification:', error);
      }
    } else if (Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          try {
            const notification = new Notification(title, {
              body: body,
              icon: '/favicon.ico',
              badge: '/favicon.ico',
              tag: 'webchat-message'
            });
            
            notification.onclick = () => {
              window.focus();
              notification.close();
            };
          } catch (error) {
            console.error('[SignalRProvider] Error creating notification:', error);
          }
        }
      }).catch(error => {
        console.error('[SignalRProvider] Error requesting notification permission:', error);
      });
    }
  };

  // Helper function to play notification sound with throttling
  const lastSoundRef = useRef(0);
  const SOUND_COOLDOWN = 1000; // 1 second between sounds
  
  const playNotificationSound = () => {
    const now = Date.now();
    
    // Throttle sound to prevent spam
    if (now - lastSoundRef.current < SOUND_COOLDOWN) {
      return;
    }
    
    lastSoundRef.current = now;
    
    try {
      // Use Web Audio API directly instead of file (more reliable)
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Create a pleasant notification tone
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      // Silent failure for notification sound
    }
  };

  // Function to check and request notification permissions
  const checkNotificationPermission = async () => {
    if (!window.Notification) {
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      return false;
    }

    // Permission is 'default', request it
    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        // Show a test notification
        setTimeout(() => {
          showNotification('WebChat Notifications Enabled', 'You will now receive real-time message notifications');
        }, 1000);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('[SignalRProvider] Error requesting notification permission:', error);
      return false;
    }
  };

  // Request browser notification permission on mount (non-blocking)
  useEffect(() => {
    // Request notification permission silently without blocking UI
    const requestNotificationPermission = async () => {
      if (!window.Notification) {
        // Browser doesn't support notifications - silent fallback to sound only
        return;
      }
      
      if (Notification.permission === 'default') {
        try {
          const permission = await Notification.requestPermission();
          
          if (permission === 'granted') {
            // Show a subtle confirmation notification
            setTimeout(() => {
              try {
                const testNotification = new Notification('WebChat Notifications Enabled', {
                  body: 'You will receive desktop alerts for new messages',
                  icon: '/favicon.ico',
                  badge: '/favicon.ico',
                  tag: 'webchat-test',
                  silent: true
                });
                
                testNotification.onclick = () => {
                  window.focus();
                  testNotification.close();
                };
                
                // Auto-close after 4 seconds
                setTimeout(() => {
                  testNotification.close();
                }, 4000);
              } catch (notifError) {
                // Silent failure for notification display
              }
            }, 500);
          }
          // If denied, silently fall back to sound-only alerts
        } catch (permError) {
          // Silent failure - browser may block programmatic permission requests
        }
      }
    };
    
    // Run permission check after a short delay to ensure page is loaded
    setTimeout(requestNotificationPermission, 1000);
    
    // Also check with the existing function
    checkNotificationPermission();
    // Fetch operator info (from JWT or API)
    const fetchOperatorInfo = async () => {
      try {
        const token = accessToken || localStorage.getItem('token');
        if (!token) {
          return;
        }

        let operatorId = null;
        let userRole = null;
        let clientId = null;
        
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          operatorId = payload.nameid;
          userRole = payload.role;
          clientId = payload.ClientId;
        } catch (jwtError) {
          console.error('[SignalRProvider] Failed to decode JWT:', jwtError);
          return;
        }

        // Only fetch operator info for operator roles, skip for consumers
        if (userRole === 'Operator' || userRole === 'Admin' || userRole === 'ClientAdmin') {
          if (operatorId) {
            try {
              const res = await api.get(`/operators/${operatorId}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              setOperatorInfo({
                ...res.data,
                userRole,
                clientId: clientId || res.data.clientId
              });
            } catch (apiError) {
              // Fallback to JWT data if API call fails
              setOperatorInfo({
                id: operatorId,
                userRole,
                clientId
              });
            }
          }
        } else {
          // For consumers or other roles, just set basic info from JWT
          setOperatorInfo({
            id: operatorId,
            userRole,
            clientId
          });
        }
      } catch (err) {
        console.error('Failed to fetch operator info:', err);
      }
    };
    fetchOperatorInfo();
  }, [accessToken]);

  const connectionRef = useRef(null);
  const latestTokenRef = useRef(accessToken);
  const [isConnected, setIsConnected] = React.useState(false);

  useEffect(() => {
    latestTokenRef.current = accessToken || localStorage.getItem('token');
  }, [accessToken]);

  useEffect(() => {
    const createAndStartConnection = async () => {
      // Get the current token
      const token = accessToken || localStorage.getItem('token');
      
      if (!token) {
        return;
      }

      // Wait for operatorInfo to be available before creating connection
      if (!operatorInfo) {
        return;
      }
      
      if (connectionRef.current) {
        return;
      }
      
      // Get ClientID for the connection
      let clientIdForConnection = null;
      const userRole = operatorInfo?.userRole;
      
      // First try to get from operatorInfo
      if (operatorInfo && operatorInfo.clientId) {
        clientIdForConnection = operatorInfo.clientId;
      } else {
        // Fallback: try to decode from current token
        try {
          const currentToken = latestTokenRef.current || localStorage.getItem('token');
          if (currentToken) {
            const payload = JSON.parse(atob(currentToken.split('.')[1]));
            clientIdForConnection = payload.ClientId;
          }
        } catch (e) {
          // Silent failure
        }
      }
      
      // Build SignalR URL based on user role
      let signalRUrl = `${apiUrl}/hubs/messaging`;
      
      if (userRole === 'SuperAdmin') {
        // SuperAdmin connects with special role parameter - backend will add to SuperAdmins group
        signalRUrl += `?role=SuperAdmin`;
        console.log('[SignalRProvider] SuperAdmin detected - connecting with role=SuperAdmin parameter');
      } else if (clientIdForConnection) {
        // Regular operators/ClientAdmin connect with their ClientID
        signalRUrl += `?ClientID=${clientIdForConnection}`;
      } else {
        console.warn('[SignalRProvider] No ClientID found in JWT token or operatorInfo - connecting without ClientID parameter');
      }
      
      // Build connection
      const connection = new HubConnectionBuilder()
        .withUrl(signalRUrl, {
          accessTokenFactory: () => {
            const currentToken = latestTokenRef.current || localStorage.getItem('token');
            if (currentToken) {
              // Validate token format
              const parts = currentToken.split('.');
              if (parts.length !== 3) {
                console.error('[SignalRProvider] Invalid JWT token format');
                return '';
              }
            }
            return currentToken || '';
          }
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            // Stop retrying after 10 attempts (prevents infinite reconnection loops)
            if (retryContext.previousRetryCount >= 10) {
              return null; // Stop reconnecting
            }
            // Exponential backoff: 0, 2s, 4s, 8s, 16s, 30s (capped)
            const delay = Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
            return delay;
          }
        })
        .build();

      connection.onclose(() => setIsConnected(false));
      
      // Track active conversation groups to rejoin on reconnect
      const activeConversationsRef = { current: new Set() };
      
      connection.onreconnected(async (connectionId) => {
        setIsConnected(true);
        
        // Re-join all active conversation groups
        if (activeConversationsRef.current.size > 0) {
          for (const convId of activeConversationsRef.current) {
            try {
              await connection.invoke('JoinConversation', convId);
            } catch (error) {
              console.error('[SignalRProvider] Failed to re-join conversation:', convId, error);
            }
          }
        }
        
        // Dispatch event so components can refresh their data
        window.dispatchEvent(new CustomEvent('signalr-reconnected', { detail: { connectionId } }));
      });
      
      connection.onreconnecting((error) => {
        setIsConnected(false);
        window.dispatchEvent(new CustomEvent('signalr-reconnecting', { detail: { error } }));
      });
      
      // Expose method to track active conversations for reconnection
      connection.trackConversation = (conversationId) => {
        activeConversationsRef.current.add(conversationId);
      };
      
      connection.untrackConversation = (conversationId) => {
        activeConversationsRef.current.delete(conversationId);
      };

      // Handle MessageReceived events (future enhancement)
      connection.on('MessageReceived', async (data) => {
        window.dispatchEvent(new CustomEvent('signalr-message-received', { detail: data }));
      });

      // Handle ReceiveMessage events (primary message event from backend)
      connection.on('ReceiveMessage', (message) => {
        // Normalize property names (backend uses PascalCase, frontend uses camelCase)
        const normalizedMessage = {
          id: message.id || message.Id,
          conversationId: message.conversationId || message.ConversationId,
          consumerId: message.consumerId || message.ConsumerId,
          content: message.content || message.Content,
          direction: (message.direction || message.Direction || '').toLowerCase(),
          timestamp: message.timestamp || message.Timestamp || message.createdAt || message.CreatedAt || message.sentAt || message.SentAt,
          senderName: message.senderName || message.SenderName,
          status: message.status || message.Status,
          ...message
        };
        
        window.dispatchEvent(new CustomEvent('signalr-receive-message', { detail: normalizedMessage }));
      });

      // Handle MessageClaimed events (operator queue management)
      connection.on('MessageClaimed', (data) => {
        window.dispatchEvent(new CustomEvent('signalr-message-claimed', { detail: data }));
      });

      // Handle MessageReleased events
      connection.on('MessageReleased', (data) => {
        window.dispatchEvent(new CustomEvent('signalr-message-released', { detail: data }));
      });

      // Handle MessageReplied events (operator responses with credit updates)
      connection.on('MessageReplied', (data) => {
        window.dispatchEvent(new CustomEvent('signalr-message-replied', { detail: data }));
      });

      // Handle ConversationLocked events
      connection.on('ConversationLocked', (data) => {
        window.dispatchEvent(new CustomEvent('signalr-conversation-locked', { detail: data }));
      });

      // Handle ConversationUnlocked events
      connection.on('ConversationUnlocked', (data) => {
        window.dispatchEvent(new CustomEvent('signalr-conversation-unlocked', { detail: data }));
      });

      // Handle QueueStats updates
      connection.on('QueueStats', (data) => {
        window.dispatchEvent(new CustomEvent('signalr-queue-stats', { detail: data }));
      });

      // Handle Presence updates
      connection.on('Presence', (data) => {
        window.dispatchEvent(new CustomEvent('signalr-presence', { detail: data }));
      });

      // Handle OnlineOperatorCount updates (live logged-in operator count)
      connection.on('OnlineOperatorCount', (count) => {
        window.dispatchEvent(new CustomEvent('signalr-online-operator-count', { detail: count }));
      });

      // Handle Typing indicators
      connection.on('Typing', (data) => {
        window.dispatchEvent(new CustomEvent('signalr-typing', { detail: data }));
      });

      // Handle NewMessageNotification events (primary notification event from backend)
      connection.on('NewMessageNotification', (payload) => {
        // Add deduplication for SignalR events to prevent duplicate processing
        const eventKey = `${payload.conversationId || payload.ConversationId}-${payload.messagePreview || payload.MessagePreview}-${Date.now()}`;
        const eventWindow = 1000; // 1 second window for duplicate events
        
        if (!window.signalREventCache) {
          window.signalREventCache = new Map();
        }
        
        // Clean old entries
        const now = Date.now();
        for (const [key, timestamp] of window.signalREventCache.entries()) {
          if (now - timestamp > eventWindow) {
            window.signalREventCache.delete(key);
          }
        }
        
        // Check for recent duplicate
        const recentDuplicate = Array.from(window.signalREventCache.entries()).find(([key, timestamp]) => {
          const [convId, preview] = key.split('-');
          const currentConvId = payload.conversationId || payload.ConversationId;
          const currentPreview = payload.messagePreview || payload.MessagePreview;
          return convId === String(currentConvId) && preview === currentPreview && (now - timestamp) < eventWindow;
        });
        
        if (recentDuplicate) {
          return; // Skip duplicate event
        }
        
        // Record this event
        window.signalREventCache.set(eventKey, now);
        
        // Create notification title and body
        let notificationTitle = '💬 New Consumer Message';
        let notificationBody = '';
        
        if (payload.ClientId) {
          notificationTitle = `💬 New Message - Client ${payload.ClientId}`;
        }
        
        if (payload.MessagePreview || payload.messagePreview) {
          notificationBody = `${payload.MessagePreview || payload.messagePreview}`;
        } else {
          notificationBody = 'New message received from consumer';
        }
        
        if (payload.ConversationId) {
          notificationBody += ` (Conversation #${payload.ConversationId})`;
        }
        
        // Show browser notification and play sound (not snackbar)
        showNotification(notificationTitle, notificationBody);
        playNotificationSound();
        
        // Visual indicators in browser tab
        const originalTitle = document.title;
        document.title = '🔴 New Message - WebChat';
        
        // Update favicon temporarily
        try {
          const link = document.querySelector("link[rel~='icon']");
          if (link) {
            const originalHref = link.href;
            link.href = '/favicon.ico';
            setTimeout(() => {
              link.href = originalHref;
            }, 3000);
          }
        } catch (e) {
          // Silent failure for favicon update
        }
        
        // Reset title after 10 seconds or when user focuses the window
        const resetTitle = () => {
          document.title = originalTitle || 'WebChat';
        };
        
        const titleResetTimer = setTimeout(resetTitle, 10000);
        
        // Reset immediately if user focuses the window
        const handleFocus = () => {
          clearTimeout(titleResetTimer);
          resetTitle();
          window.removeEventListener('focus', handleFocus);
        };
        
        window.addEventListener('focus', handleFocus);
        
        // Dispatch custom event for components to handle
        window.dispatchEvent(new CustomEvent('signalr-new-message-notification', { detail: payload }));
      });

      connectionRef.current = connection;

      // Start the connection
      try {
        await connection.start();
        setIsConnected(true);
      } catch (err) {
        setIsConnected(false);
        console.error('[SignalRProvider] SignalR connection failed:', err.message);
        
        // Clear the connection ref so it can be retried
        connectionRef.current = null;
        
        // Retry connection after a delay for different types of errors
        if (err.message && (err.message.includes('401') || err.message.includes('Unauthorized'))) {
          setTimeout(() => {
            latestTokenRef.current = localStorage.getItem('token');
            createAndStartConnection();
          }, 2000);
        } else if (err.message && (err.message.includes('Failed to complete negotiation') || err.message.includes('transport'))) {
          setTimeout(() => {
            createAndStartConnection();
          }, 3000);
        }
      }
    };

    createAndStartConnection();

    // Listen for logout event to explicitly stop SignalR connection
    // This ensures OnDisconnectedAsync fires on the server and updates the operator count
    const handleLogout = async () => {
      if (connectionRef.current && connectionRef.current.state === HubConnectionState.Connected) {
        try {
          await connectionRef.current.stop();
        } catch (err) {
          console.error('[SignalRProvider] Error stopping connection on logout:', err);
        }
        connectionRef.current = null;
        setIsConnected(false);
      }
    };
    window.addEventListener('app-logout', handleLogout);

    return () => {
      window.removeEventListener('app-logout', handleLogout);
      if (connectionRef.current) {
        connectionRef.current.stop();
        connectionRef.current = null;
      }
    };
  }, [accessToken, operatorInfo]);

  // Separate effect to join groups when operatorInfo becomes available
  useEffect(() => {
    const joinGroupsWhenReady = async () => {
      if (connectionRef.current && isConnected && operatorInfo) {
        const { userRole, clientId } = operatorInfo;
        
        if ((userRole === 'Operator' || userRole === 'Admin' || userRole === 'ClientAdmin') && clientId) {
          if (!joinedClientIdsRef.current.has(clientId)) {
            try {
              await connectionRef.current.invoke('JoinClient', clientId);
              joinedClientIdsRef.current.add(clientId);
            } catch (err) {
              console.error(`[SignalRProvider] Failed to join client:${clientId} group:`, err);
            }
          }
        }
      }
    };

    joinGroupsWhenReady();
  }, [operatorInfo, isConnected]); // Join groups when operatorInfo or connection status changes

  // Provide connection and helper methods in context
  const contextValue = {
    connection: connectionRef.current,
    isConnected,
    // Helper methods for components
    joinClient: async (clientId) => {
      if (connectionRef.current && isConnected) {
        try {
          await connectionRef.current.invoke('JoinClient', clientId);
          joinedClientIdsRef.current.add(clientId);
        } catch (err) {
          console.error(`[SignalRProvider] Failed to join client:${clientId}:`, err);
        }
      }
    },
    leaveClient: async (clientId) => {
      if (connectionRef.current && isConnected) {
        try {
          await connectionRef.current.invoke('LeaveClient', clientId);
          joinedClientIdsRef.current.delete(clientId);
        } catch (err) {
          console.error(`[SignalRProvider] Failed to leave client:${clientId}:`, err);
        }
      }
    },
    joinConversation: async (conversationId) => {
      if (connectionRef.current && isConnected) {
        try {
          await connectionRef.current.invoke('JoinConversation', conversationId);
        } catch (err) {
          console.error(`[SignalRProvider] Failed to join conversation:${conversationId}:`, err);
        }
      }
    },
    leaveConversation: async (conversationId) => {
      if (connectionRef.current && isConnected) {
        try {
          await connectionRef.current.invoke('LeaveConversation', conversationId);
        } catch (err) {
          console.error(`[SignalRProvider] Failed to leave conversation:${conversationId}:`, err);
        }
      }
    },
    claimMessage: async (messageId) => {
      if (connectionRef.current && isConnected) {
        try {
          await connectionRef.current.invoke('ClaimMessage', messageId);
        } catch (err) {
          console.error(`[SignalRProvider] Failed to claim message:${messageId}:`, err);
        }
      }
    },
    releaseMessage: async (messageId) => {
      if (connectionRef.current && isConnected) {
        try {
          await connectionRef.current.invoke('ReleaseMessage', messageId);
        } catch (err) {
          console.error(`[SignalRProvider] Failed to release message:${messageId}:`, err);
        }
      }
    },
    sendOperatorReply: async (conversationId, text) => {
      if (connectionRef.current && isConnected) {
        try {
          await connectionRef.current.invoke('SendOperatorReply', conversationId, text);
        } catch (err) {
          console.error(`[SignalRProvider] Failed to send operator reply:`, err);
        }
      }
    },
    sendConsumerMessage: async (conversationId, text) => {
      if (connectionRef.current && isConnected) {
        try {
          await connectionRef.current.invoke('SendConsumerMessage', conversationId, text);
        } catch (err) {
          console.error(`[SignalRProvider] Failed to send consumer message:`, err);
        }
      }
    },
    reportPresence: async (status) => {
      if (connectionRef.current && isConnected) {
        try {
          await connectionRef.current.invoke('ReportPresence', status);
        } catch (err) {
          console.error(`[SignalRProvider] Failed to report presence:`, err);
        }
      }
    },
    sendTyping: async (conversationId, actor) => {
      if (connectionRef.current && isConnected) {
        try {
          await connectionRef.current.invoke('Typing', conversationId, actor);
        } catch (err) {
          console.error(`[SignalRProvider] Failed to send typing indicator:`, err);
        }
      }
    },
    // Debug function to check SignalR status
    getDebugInfo: () => {
      const token = accessToken || localStorage.getItem('token');
      return {
        connectionExists: !!connectionRef.current,
        connectionState: connectionRef.current?.state || 'No connection',
        connectionId: connectionRef.current?.connectionId || 'N/A',
        isConnected,
        operatorInfo,
        joinedClientIds: Array.from(joinedClientIdsRef.current),
        currentPage: window.location.pathname,
        hasToken: !!token,
        tokenPreview: token ? token.substring(0, 50) + '...' : 'No token',
        backendUrl: `${apiUrl}/hubs/messaging`,
        notificationSupport: !!window.Notification,
        notificationPermission: window.Notification ? Notification.permission : 'Not supported'
      };
    },
    // Test notification function
    testNotification: () => {
      showNotification('WebChat Test Notification', 'This is a test notification to verify browser notifications are working');
      playNotificationSound();
    },
    // Check notification permission function
    checkNotificationPermission
  };

  // Expose debug info to window for console debugging
  if (typeof window !== 'undefined') {
    window.signalRDebug = contextValue.getDebugInfo;
    window.testNotification = contextValue.testNotification;
    window.checkNotificationPermission = contextValue.checkNotificationPermission;
  }

  return (
    <SignalRContext.Provider value={contextValue}>
      {children}
    </SignalRContext.Provider>
  );
};

export const useSignalRContext = () => useContext(SignalRContext);
